import JsPDF from "jspdf";

/**
 * One-page GPS report, laid out like the Tableau export the academies already
 * read: a dark header strip of squad KPIs, then a ranked bar panel per metric
 * with the squad-average line.
 *
 * Drawn as vectors rather than a screenshot so the text stays selectable and
 * the file stays small — these get emailed around.
 */

const PAGE = { w: 210, h: 297, margin: 10 };
const INK = { dark: [17, 17, 17], mid: [108, 117, 125], light: [233, 236, 239], white: [255, 255, 255] };

/**
 * Red -> amber -> green across the range actually present, which is what the
 * Tableau scale does. Colouring against the squad average instead put almost
 * every player in one green band — technically true, visually useless, and it
 * hid the spread the report exists to show.
 */
function barColour(value, min, max) {
  if (value == null || max == null || max === min) return [124, 179, 66];
  const t = Math.max(0, Math.min(1, (Number(value) - min) / (max - min)));
  const stops = [
    [220, 53, 69],   // 0.0 red
    [246, 170, 30],  // 0.5 amber
    [25, 135, 84],   // 1.0 green
  ];
  const seg = t < 0.5 ? 0 : 1;
  const local = t < 0.5 ? t / 0.5 : (t - 0.5) / 0.5;
  const a = stops[seg];
  const b = stops[seg + 1];
  return [0, 1, 2].map((i) => Math.round(a[i] + (b[i] - a[i]) * local));
}

/** "Scott Douglas Gilmour" -> "S Gilmour" once it stops fitting. */
function shortName(name, limit = 18) {
  const n = String(name || "").trim();
  if (n.length <= limit) return n;
  const parts = n.split(/\s+/);
  if (parts.length < 2) return n.slice(0, limit);
  const short = `${parts[0][0]} ${parts[parts.length - 1]}`;
  return short.length <= limit ? short : short.slice(0, limit);
}

function fmt(v, key) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (key === "top_speed_kph") return n.toFixed(1);
  if (key === "total_distance_m" && n >= 1000) return (n / 1000).toFixed(2) + "K";
  return Math.round(n).toLocaleString();
}

/**
 * @param {object} opts
 *   clubName, subtitle, players[], squad{}, metrics{}, metricOrder[], maxRows
 */
export function buildGpsReportPdf(opts) {
  const {
    clubName = "GPS report",
    subtitle = "",
    players = [],
    squad = {},
    metrics = {},
    metricOrder = [],
    maxRows = 15,
  } = opts;

  const doc = new JsPDF({ unit: "mm", format: "a4" });

  // ---- header -------------------------------------------------------------
  doc.setFillColor(...INK.dark);
  doc.rect(0, 0, PAGE.w, 30, "F");

  doc.setTextColor(...INK.white);
  doc.setFont("helvetica", "bold");
  // Shrink rather than collide: "Partick Thistle F.C." at 15pt ran straight
  // into the first KPI label.
  let nameSize = 15;
  doc.setFontSize(nameSize);
  const nameLimit = 74;
  while (doc.getTextWidth(clubName) > nameLimit && nameSize > 9) {
    nameSize -= 0.5;
    doc.setFontSize(nameSize);
  }
  doc.text(clubName, PAGE.margin, 13);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(200, 200, 200);
  if (subtitle) doc.text(subtitle, PAGE.margin, 19);
  doc.text(
    `Generated ${new Date().toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}`,
    PAGE.margin,
    24.5
  );

  // Squad KPIs across the right of the header — the strip on the Tableau report.
  const kpis = metricOrder.slice(0, 4).filter((k) => squad && squad[k] != null);
  const kpiStart = PAGE.margin + nameLimit + 6;
  const kpiW = kpis.length ? (PAGE.w - PAGE.margin - kpiStart) / kpis.length : 0;
  let kx = kpiStart;
  kpis.forEach((k) => {
    doc.setTextColor(150, 150, 150);
    doc.setFontSize(6.5);
    // Clip to the column: "HIGH INTENSITY RUNNING" ran into the next KPI.
    let lab = (metrics[k] && metrics[k].label ? metrics[k].label : k).toUpperCase();
    while (doc.getTextWidth(lab) > kpiW - 3 && lab.length > 4) {
      lab = lab.slice(0, -1);
    }
    doc.text(lab, kx, 13);
    doc.setTextColor(...INK.white);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.text(String(fmt(squad[k], k)), kx, 20);
    doc.setFont("helvetica", "normal");
    kx += kpiW;
  });

  // ---- metric panels ------------------------------------------------------
  const cols = 2;
  const panelW = (PAGE.w - PAGE.margin * 2 - 8) / cols;
  const panelH = 74;
  let px = PAGE.margin;
  let py = 38;

  metricOrder.forEach((key, i) => {
    if (i > 0 && i % cols === 0) {
      px = PAGE.margin;
      py += panelH;
    }
    if (py + panelH > PAGE.h - 12) {
      doc.addPage();
      py = PAGE.margin + 4;
      px = PAGE.margin;
    }

    const meta = metrics[key] || {};
    const ranked = players
      .filter((p) => p[key] !== null && p[key] !== undefined && p[key] !== "")
      .sort((a, b) => Number(b[key]) - Number(a[key]))
      .slice(0, maxRows);

    doc.setTextColor(...INK.dark);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.text(`${meta.label || key}${meta.unit ? ` (${meta.unit})` : ""}`, px, py);

    // Bar LENGTH scales to the rows shown, but COLOUR scales across the whole
    // squad — otherwise trimming to the top 15 compresses the scale and a
    // player sitting on the squad average is painted red as "worst on the
    // page". The Tableau report colours against the full range too.
    const shownVals = ranked.map((p) => Number(p[key]));
    const allVals = players
      .map((p) => Number(p[key]))
      .filter((v) => !Number.isNaN(v) && v !== null);
    const max = shownVals.length ? Math.max(...shownVals) : 0;
    const colourMin = allVals.length ? Math.min(...allVals) : 0;
    const colourMax = allVals.length ? Math.max(...allVals) : 0;
    const labelW = 26;
    const valueW = 13;
    const trackX = px + labelW;
    const trackW = panelW - labelW - valueW - 2;
    const rowH = 3.7;
    let ry = py + 4;

    doc.setFont("helvetica", "normal");
    ranked.forEach((p) => {
      doc.setFontSize(6);
      doc.setTextColor(...INK.mid);
      doc.text(shortName(p.name), px, ry + 2.5);

      doc.setFillColor(...INK.light);
      doc.rect(trackX, ry, trackW, rowH - 1.2, "F");

      const w = max ? Math.max(0.6, (Number(p[key]) / max) * trackW) : 0;
      doc.setFillColor(...barColour(p[key], colourMin, colourMax));
      doc.rect(trackX, ry, w, rowH - 1.2, "F");

      doc.setTextColor(...INK.dark);
      doc.text(String(fmt(p[key], key)), px + panelW - 1, ry + 2.5, { align: "right" });
      ry += rowH;
    });

    // The dashed squad-average line, as on the report the coaches know.
    if (squad && squad[key] != null && max) {
      const ax = trackX + (Number(squad[key]) / max) * trackW;
      doc.setDrawColor(134, 142, 150);
      doc.setLineDashPattern([0.8, 0.8], 0);
      doc.line(ax, py + 3.2, ax, ry - 0.8);
      doc.setLineDashPattern([], 0);
    }

    if (players.length > maxRows) {
      doc.setFontSize(5.5);
      doc.setTextColor(...INK.mid);
      doc.text(`top ${maxRows} of ${players.length}`, px, ry + 2.5);
    }

    px += panelW + 8;
  });

  // ---- footer -------------------------------------------------------------
  doc.setFontSize(6);
  doc.setTextColor(...INK.mid);
  doc.text(
    "Dashed line = squad average. Bar colour runs red (lowest) to green (highest) within each metric.",
    PAGE.margin,
    PAGE.h - 7
  );

  return doc;
}
